// lib/features/search/search.thunk.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import {
  fetchHomeSearchList,
  saveSearch as saveSearchService,
  clearSearchHistory as clearSearchHistoryService,
  bulkDeleteSearches as bulkDeleteSearchesService,
  fetchSearchResults as fetchSearchResultsService,
  getFavoriteSearches as getFavoriteSearchesService,
  fetchSearchSuggestions as fetchSearchSuggestionsService,
  fetchImageSearchResults as fetchImageSearchResultsService,
} from "./search.service";
import {
  SaveSearchRequest,
  SaveSearchData,
  BulkDeleteSearchRequest,
  SearchSection,
  SearchResultsRequest,
  SearchResultsData,
  GetFavoriteSearchesRequest,
  FavoriteSearchesData,
  SearchSuggestionsData,
  GetSearchSuggestionsRequest,
  ImageSearchRequest,
} from "./search.types";
import { handleApiError } from "@/app/utils/handleApiError";

export const getHomeSearchList = createAsyncThunk<
  { placeholders: string[]; items: SearchSection[] },
  void,
  { rejectValue: string; state: RootState }
>(
  "search/getHomeSearchList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchHomeSearchList();
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
  {
    condition: (_, { getState }) =>
      getState().search.status !== "loading",
  },
);

export const saveSearch = createAsyncThunk<
  SaveSearchData,
  SaveSearchRequest,
  { rejectValue: string }
>("search/saveSearch", async (data, { rejectWithValue }) => {
  try {
    const response = await saveSearchService(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const clearSearchHistory = createAsyncThunk<
  null,
  void,
  { rejectValue: string }
>("search/clearSearchHistory", async (_, { rejectWithValue }) => {
  try {
    await clearSearchHistoryService();
    return null;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const bulkDeleteSearches = createAsyncThunk<
  null,
  BulkDeleteSearchRequest,
  { rejectValue: string }
>("search/bulkDeleteSearches", async (data, { rejectWithValue }) => {
  try {
    await bulkDeleteSearchesService(data);
    return null;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getSearchResults = createAsyncThunk<
  SearchResultsData,
  SearchResultsRequest,
  { rejectValue: string }
>("search/getSearchResults", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchSearchResultsService(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getFavoriteSearches = createAsyncThunk<
  FavoriteSearchesData,
  GetFavoriteSearchesRequest | undefined,
  { rejectValue: string }
>("search/getFavoriteSearches", async (params, { rejectWithValue }) => {
  try {
    const response = await getFavoriteSearchesService(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getSearchSuggestions = createAsyncThunk<
  SearchSuggestionsData,
  GetSearchSuggestionsRequest,
  { rejectValue: string; state: RootState }
>(
  "search/getSearchSuggestions",
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchSearchSuggestionsService(params.search);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
  {
    condition: (_, { getState }) =>
      getState().search.suggestionsStatus !== "loading",
  },
);

export const getImageSearchResults = createAsyncThunk<
  SearchResultsData,
  ImageSearchRequest,
  { rejectValue: string }
>("search/getImageSearchResults", async ({ image, page, per_page }, { rejectWithValue }) => {
  try {
    const response = await fetchImageSearchResultsService(image, page, per_page);
    return response.data;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});
